import { urls } from "@selector/shared";
import { ErrorBoundary } from "react-error-boundary";
import { Card, CardRowDetail, CardSection, Center, ColoredLabel, HStack, MarkdownPreview, SymbolIcon } from "widgets";

import { type ActivityInstance } from "@/graphql.ts";
import { FallbackPanel } from "@/modules/workflow/components/ErrorFallbackPanel.tsx";
import { VersionTag } from "@/modules/workflow/components/VersionTag.tsx";
import { getComputingIcon, getComputingLabel } from "@/modules/workflow/utils/computing.ts";
import type { Activity } from "@/modules/workflow/utils/types.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface PreviewActivityInfoRendererProps {
  selectedActivity: (ActivityInstance & { label: string }) | undefined;
  processorActivities: Activity[];
}

export const PreviewActivityInfoRenderer = (props: PreviewActivityInfoRendererProps) => {
  if (!props.selectedActivity) {
    return (
      <Card noBorder fillHeight>
        <Center height="100%">
          <ContentPlaceholder mini>Select activity to show parameters</ContentPlaceholder>
        </Center>
      </Card>
    );
  }

  const activityDefinition = props.processorActivities?.find(
    (a) => a.name === props.selectedActivity?.name && a.version.name === props.selectedActivity?.version,
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackPanel}>
      <Card
        noBorder
        scrollable
        title="Activity Information"
        subtitle={props.selectedActivity?.label}
        data-testid="activity-info"
      >
        <CardRowDetail title="Name" value={props.selectedActivity?.label} />
        {activityDefinition?.description != null && (
          <CardRowDetail title="Description" value={activityDefinition.description} data-testid="description" />
        )}
        {activityDefinition?.tags != null && (
          <CardRowDetail title="Tags" data-testid="tags">
            <HStack wrap spacing={10}>
              {activityDefinition.tags?.map((tag) => (
                <ColoredLabel label={tag.name} color={tag.color ?? undefined} key={tag.name} />
              ))}
            </HStack>
          </CardRowDetail>
        )}
        <CardRowDetail
          title="Version"
          value={
            <VersionTag
              draggable={false}
              version={props.selectedActivity?.version}
              // @ts-expect-error Typescript being stupid. This property exists...
              activityName={props.selectedActivity?.name}
              color={activityDefinition?.version.color}
              key={`activity-version-tag-${props.selectedActivity.identifier}`}
            />
          }
        />
        {props.selectedActivity?.executor != undefined && (
          <CardRowDetail
            title="Computing Environment"
            value={
              <HStack>
                <SymbolIcon
                  icon={getComputingIcon(props.selectedActivity?.executor)}
                  size={16}
                  style={{ color: "var(--primary-light)" }}
                />
                {getComputingLabel(props.selectedActivity?.executor)}
              </HStack>
            }
          />
        )}
        {!!activityDefinition?.path && (
          <CardRowDetail title="Location" data-testid="location">
            {activityDefinition.path.split("/").join(" / ")}
          </CardRowDetail>
        )}
        {!!activityDefinition?.readme && (
          <CardSection title="Readme" data-testid="readme">
            <CardRowDetail>
              <MarkdownPreview markdownText={activityDefinition.readme} baseUrl={urls.basePath} />
            </CardRowDetail>
          </CardSection>
        )}
      </Card>
    </ErrorBoundary>
  );
};
